import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQueryClient } from 'react-query';
import { Music, Users, MapPin, ArrowRight, QrCode, Crown, X, Settings, User } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useGeolocation } from '../hooks/useGeolocation';
import { useNearbySessions } from '../hooks/useNearbySessions';
import api from '../utils/api';
import toast from 'react-hot-toast';
import PaymentModal from './PaymentModal';
import { AvatarStack } from './Avatar';

const Welcome = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading, updateGuestName } = useAuth(); // Only needed for host features
  const { location, getLocation, error: locationError, errorDetail: locationErrorDetail } = useGeolocation();
  
  const [sessionId, setSessionId] = useState('');
  const [guestName, setGuestName] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [isHostMode, setIsHostMode] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Session payload held while the Stripe payment modal is open (402 flow)
  const [paymentPrompt, setPaymentPrompt] = useState(null);
  const [form, setForm] = useState({
    name: '',
    radius: 1000,
    maxParticipants: 50,
    allowGuestVoting: true,
    musicService: 'spotify',
    requireApproval: true,
    explicitAllowed: true,
    genreRestrictions: []
  });
  
  const { data: nearbySessions, isLoading: loadingSessions, isError: sessionsError, refetch: refetchSessions } = useNearbySessions(
    location?.latitude,
    location?.longitude
  );

  const timestamp = () => new Date().toISOString();
  console.log(`[${timestamp()}] 🏠 Welcome component rendered:`, { 
    user: user?.displayName, 
    userExists: !!user,
    loading,
    userObject: user 
  });
  
  // Track page load and restore guest name if available
  useEffect(() => {
    console.log(`[${timestamp()}] 🏠 Welcome page loaded`);

    // Restore guest name from previous session if available
    const savedGuestName = sessionStorage.getItem('guestName');
    if (savedGuestName) {
      setGuestName(savedGuestName);
      console.log(`[${timestamp()}] 🏠 Restored guest name: ${savedGuestName}`);
    }
  }, []);

  // Signed-in users already have a name — prefill it so they don't have to
  // retype it every visit (still editable if they want a different one)
  useEffect(() => {
    if (user?.displayName) {
      setGuestName((prev) => prev || user.displayName);
    }
  }, [user?.displayName]);

  // The name a join will actually use: whatever is in the field, falling
  // back to the signed-in profile name
  const effectiveName = guestName.trim() || user?.displayName?.trim() || '';

  // Apply an explicitly typed name change to the signed-in profile
  const syncTypedName = async (name) => {
    if (user && user.displayName !== name) {
      try {
        await updateGuestName(name);
      } catch (err) {
        console.error('Failed to update display name:', err);
      }
    }
  };

  // Get location on component mount
  useEffect(() => {
    console.log('Component mounted, requesting location...');
    getLocation();
  }, [getLocation]);

  // Show the location help modal on ANY location failure (denied, timeout,
  // unavailable) — unless the user has already chosen to skip it
  useEffect(() => {
    if (locationError && !sessionStorage.getItem('locationSkipped')) {
      setShowLocationModal(true);
    }
  }, [locationError]);

  // Log when location is obtained
  useEffect(() => {
    if (location) {
      console.log(`Location obtained: ${location.latitude}, ${location.longitude}`);
    }
  }, [location]);

  // Don't show loading for Welcome page - it should be immediately accessible
  // The auth check only happens when user tries to create/join a session

  const handleJoinSession = async () => {
    if (!sessionId.trim()) {
      toast.error('Please enter a session ID');
      return;
    }

    if (!effectiveName) {
      toast.error('Please enter your name');
      return;
    }

    if (effectiveName.length < 2) {
      toast.error('Name must be at least 2 characters long');
      return;
    }

    if (!location) {
      toast.error('Please allow location access to join sessions');
      setShowLocationModal(true);
      return;
    }

    setIsJoining(true);
    try {
      // Store the name for the session and sync any typed change to the profile
      sessionStorage.setItem('guestName', effectiveName);
      await syncTypedName(effectiveName);

      // Navigate directly to session page - no auth required for guests
      navigate(`/session/${sessionId.trim()}`);
    } catch (error) {
      console.error('Join session error:', error);
      toast.error('Failed to join session');
    } finally {
      setIsJoining(false);
    }
  };

  const handleJoinNearbySession = async (session) => {
    if (!effectiveName) {
      toast.error('Please enter your name first');
      return;
    }

    if (!location) {
      toast.error('Please allow location access to join sessions');
      setShowLocationModal(true);
      return;
    }

    // Store the name for the session and sync any typed change to the profile
    sessionStorage.setItem('guestName', effectiveName);
    await syncTypedName(effectiveName);
    navigate(`/session/${session.id}`);
  };

  const handleCreateSession = async () => {
    // Creating sessions requires authentication (host privileges)
    if (loading) {
      toast.error('Please wait while we check your account...');
      return;
    }
    
    if (!user || user.isGuest) {
      console.log(`[${timestamp()}] ⚠️ Host needs full authentication, redirecting to login`);
      toast('Hosts need to sign in (Google or Email) to create sessions', { icon: '👑' });
      // Store the intended action and guest name so we can resume after login
      sessionStorage.setItem('intendedAction', 'create');
      if (guestName.trim()) {
        sessionStorage.setItem('guestName', guestName.trim());
      }
      navigate('/login', { replace: true });
      return;
    }

    if (!location) {
      toast.error('Location is required to create a session');
      return;
    }

    // Use form values (wizard) with sensible defaults
    const sessionData = {
      name: form.name?.trim() || `Session ${Math.random().toString(36).substring(2, 6).toUpperCase()}`,
      geofence: {
        latitude: location.latitude,
        longitude: location.longitude,
        radius: Number(form.radius) || 1000
      },
      musicService: form.musicService || 'spotify',
      maxParticipants: Number(form.maxParticipants) || 50,
      allowGuestVoting: !!form.allowGuestVoting,
      requireApproval: !!form.requireApproval,
      explicitAllowed: !!form.explicitAllowed,
      // Genre restrictions are Apple Music only
      genreRestrictions: form.musicService === 'apple' ? (form.genreRestrictions || []) : [],
      blockedSongs: []
    };

    try {
      const response = await api.post('/api/sessions', sessionData);

      const result = response.data;
      console.log('✅ Session created:', result);
      // Prime React Query cache so Session page has data immediately
      if (result?.session?.id) {
        queryClient.setQueryData(['session', result.session.id], result.session);
      }
      toast.success('Session created successfully!');
      navigate(`/session/${result.session.id}`);
    } catch (error) {
      console.error('Create session error:', error);
      if (error.response?.status === 402) {
        // Outside a free zone — hand off to the Stripe payment flow, which
        // recreates the session with the paymentId after checkout
        setPaymentPrompt(sessionData);
        return;
      }
      toast.error(error.response?.data?.error || error.message || 'Failed to create session');
    }
  };

  const formatDistance = (distance) => {
    if (distance < 1000) {
      return `${Math.round(distance)}m`;
    }
    return `${(distance / 1000).toFixed(1)}km`;
  };

  const getLocationInstructions = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    if (isIOS && isSafari) {
      return [
        '1. Tap the "AA" icon in the Safari address bar',
        '2. Tap "Website Settings"',
        '3. Tap "Location" and select "Allow"',
        '4. Refresh this page'
      ];
    } else if (isIOS) {
      return [
        '1. Go to Settings > Safari > Location',
        '2. Select "Allow" or "Ask Next Time"',
        '3. Return to this app and refresh the page'
      ];
    } else if (isAndroid) {
      return [
        '1. Tap the location icon in your browser address bar',
        '2. Select "Allow"',
        '3. Refresh this page'
      ];
    } else {
      return [
        '1. Click the location icon in your browser address bar',
        '2. Select "Allow"',
        '3. Refresh this page'
      ];
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-gray-900 to-black">
      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-lg font-semibold">Create Session</h3>
              <button onClick={()=>setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Session name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e)=>setForm(prev=>({...prev, name: e.target.value}))}
                  placeholder="Friday Night Vibes"
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Radius (meters)</label>
                <input
                  type="number"
                  min={50}
                  max={10000}
                  value={form.radius}
                  onChange={(e)=>setForm(prev=>({...prev, radius: e.target.value}))}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Max participants</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={form.maxParticipants}
                  onChange={(e)=>setForm(prev=>({...prev, maxParticipants: e.target.value}))}
                  className="input-field w-full"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-300">Allow guest voting</label>
                <input
                  type="checkbox"
                  checked={form.allowGuestVoting}
                  onChange={(e)=>setForm(prev=>({...prev, allowGuestVoting: e.target.checked}))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Music service</label>
                <select
                  value={form.musicService}
                  onChange={(e)=>setForm(prev=>({...prev, musicService: e.target.value}))}
                  className="input-field w-full"
                >
                  <option value="spotify">Spotify</option>
                  <option value="apple">Apple Music</option>
                </select>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-gray-300">Require song approval</label>
                  <p className="text-xs text-gray-400">Guest songs wait for your OK</p>
                </div>
                <input
                  type="checkbox"
                  checked={form.requireApproval}
                  onChange={(e)=>setForm(prev=>({...prev, requireApproval: e.target.checked}))}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-gray-300">Allow explicit songs</label>
                  <p className="text-xs text-gray-400">Off = clean music only</p>
                </div>
                <input
                  type="checkbox"
                  checked={form.explicitAllowed}
                  onChange={(e)=>setForm(prev=>({...prev, explicitAllowed: e.target.checked}))}
                />
              </div>
              {form.musicService === 'apple' && (
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Allowed genres</label>
                  <p className="text-xs text-gray-400 mb-2">Leave all unselected to allow everything (Apple Music only)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['Hip-Hop/Rap','Pop','R&B/Soul','Rock','Country','Dance','Electronic','Latin','Alternative','Jazz','Christian & Gospel','Reggae','K-Pop','Afrobeats'].map((genre) => {
                      const active = form.genreRestrictions.includes(genre);
                      return (
                        <button
                          key={genre}
                          type="button"
                          onClick={()=>setForm(prev=>({
                            ...prev,
                            genreRestrictions: active
                              ? prev.genreRestrictions.filter(g=>g!==genre)
                              : [...prev.genreRestrictions, genre]
                          }))}
                          className={`text-xs px-2 py-1 rounded-full transition-colors ${
                            active ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          {genre}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={()=>setShowCreateModal(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={()=>{ handleCreateSession(); setShowCreateModal(false); }} className="btn-primary flex-1">Create</button>
            </div>
          </div>
        </div>
      )}
      {/* Payment Required Modal (session creation outside free zones) */}
      <PaymentModal
        isOpen={!!paymentPrompt}
        onClose={() => setPaymentPrompt(null)}
        sessionName={paymentPrompt?.name}
        sessionData={paymentPrompt}
      />

      {/* Location Permission Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-800 rounded-lg p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Enable Location Services
              </h2>
              <button
                onClick={() => setShowLocationModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-gray-300 mb-4">
              JukeHive needs location access to help you discover nearby music sessions and ensure you're in the right area to join.
            </p>
            
            <div className="bg-gray-700 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-white mb-2">How to enable location:</h3>
              <ul className="text-gray-300 text-sm space-y-1">
                {getLocationInstructions().map((instruction, index) => (
                  <li key={index}>{instruction}</li>
                ))}
              </ul>
              <p className="text-gray-300 text-sm mt-3 pt-3 border-t border-gray-600">
                Still blocked? On iPhone also check{' '}
                <span className="text-white">Settings → Privacy &amp; Security → Location Services</span>{' '}
                (top toggle on, and <span className="text-white">Safari Websites → While Using</span>) —
                that layer overrides the website setting.
              </p>
            </div>

            {locationErrorDetail && (
              <p className="text-gray-400 text-xs mb-4 font-mono">
                Technical detail: {locationErrorDetail}
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowLocationModal(false);
                  getLocation();
                }}
                className="btn-primary flex-1"
              >
                Try Again
              </button>
              <button
                onClick={() => {
                  sessionStorage.setItem('locationSkipped', 'true');
                  setShowLocationModal(false);
                }}
                className="btn-secondary flex-1"
              >
                Skip for Now
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">



        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-4">
            <Music className="w-12 h-12 text-purple-400 mr-3" />
            <h1 className="text-4xl font-bold">
              Juke<span className="jh-gradient-text">Hive</span>
            </h1>
          </div>
          <p className="text-sm tracking-[0.35em] uppercase jh-gradient-text font-semibold mb-4">
            Connect. Vote. Vibe.
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white max-w-2xl mx-auto leading-tight mb-3">
            The music belongs to <span className="jh-gradient-text">everyone.</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Join the party! Suggest songs, vote on what plays next, and vibe together.
          </p>
        </motion.div>

        {/* Host/Guest Mode Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-md mx-auto mb-8"
        >
          <div className="bg-gray-800 rounded-lg p-1 flex">
            <button
              onClick={() => setIsHostMode(false)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                !isHostMode 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Guest Mode
            </button>
            <button
              onClick={() => setIsHostMode(true)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
                isHostMode 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Crown className="w-4 h-4 mr-1" />
              Host Mode
            </button>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {isHostMode ? (
            /* Host Mode Content */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card text-center"
            >
              <Crown className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold mb-4">Host a Music Session</h2>
              <p className="text-gray-400 mb-6">
                Create a new session and let guests join your party!
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary text-lg px-8 py-3"
              >
                Create New Session
              </button>
            </motion.div>
          ) : (
            /* Guest Mode Content */
            <React.Fragment>
              {/* Guest Name Input */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card mb-6"
              >
                <h2 className="text-xl font-semibold mb-3 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Your Name
                </h2>
                <input
                  type="text"
                  placeholder="Enter your name for the session"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className={`input-field w-full ${
                    guestName.trim().length === 1 ? 'border-red-500 focus:border-red-500' : ''
                  }`}
                  maxLength={30}
                  minLength={2}
                  aria-describedby="name-hint"
                />
                {guestName.trim().length === 1 ? (
                  <p id="name-hint" className="text-xs text-red-400 mt-1 font-medium">
                    One more letter — names need at least 2 characters
                  </p>
                ) : (
                  <p id="name-hint" className="text-xs text-gray-400 mt-1">
                    At least 2 characters — this is how the party sees you
                  </p>
                )}
              </motion.div>

              {/* Join by Session ID */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card mb-8"
              >
                <h2 className="text-2xl font-semibold mb-4 flex items-center">
                  <QrCode className="w-6 h-6 mr-2" />
                  Join by Session ID
                </h2>
                <p className="text-sm text-gray-400 mb-3">
                  Got a QR code at the party? Just scan it with your camera — it opens the session directly.
                </p>
                <div className="flex gap-4">
                  <input
                    type="text"
                    placeholder="Enter session ID"
                    value={sessionId}
                    onChange={(e) => setSessionId(e.target.value)}
                    className="input-field flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && handleJoinSession()}
                  />
                  <button
                    onClick={handleJoinSession}
                    disabled={isJoining || !sessionId.trim() || !effectiveName}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isJoining ? 'Joining...' : (
                      <React.Fragment>
                        Join
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </React.Fragment>
                    )}
                  </button>
                </div>
              </motion.div>

              {/* Nearby Sessions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="card"
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-semibold flex items-center">
                    <MapPin className="w-6 h-6 mr-2" />
                    Nearby Sessions
                  </h2>
                  {!location && (
                    <button
                      onClick={() => setShowLocationModal(true)}
                      className="btn-secondary text-sm"
                    >
                      Enable Location
                    </button>
                  )}
                </div>

                {!location ? (
                  <div className="text-center py-8">
                    <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-400 mb-4">
                      Enable location access to discover nearby music sessions
                    </p>
                    <button
                      onClick={() => setShowLocationModal(true)}
                      className="btn-primary"
                    >
                      Enable Location
                    </button>
                  </div>
                ) : loadingSessions ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
                    <p className="text-gray-400 mt-2">Finding nearby sessions...</p>
                  </div>
                ) : sessionsError ? (
                  <div className="text-center py-8">
                    <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-400 mb-4">
                      Couldn't load nearby sessions — check your connection
                    </p>
                    <button onClick={() => refetchSessions()} className="btn-secondary">
                      Try Again
                    </button>
                  </div>
                ) : nearbySessions?.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {nearbySessions.map((session) => (
                      <motion.div
                        key={session.id}
                        whileHover={{ scale: 1.02 }}
                        className={`bg-gray-800/80 rounded-xl p-4 border transition-colors ${
                          effectiveName
                            ? 'border-white/10 hover:border-purple-500/60'
                            : 'border-red-500/60 opacity-60'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-semibold text-white truncate text-lg" title={session.name}>
                            {session.name}
                          </h3>
                          <span className="flex items-center gap-1 text-[10px] font-bold text-green-400 bg-green-500/10 border border-green-500/30 rounded-full px-2 py-0.5 shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                            LIVE
                          </span>
                        </div>

                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-sm text-gray-300 mb-1.5">
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1 text-purple-400" />
                            {session.participantCount ?? Object.keys(session.participants || {}).length} vibing
                          </span>
                          <span className="flex items-center">
                            <Music className="w-4 h-4 mr-1 text-purple-400" />
                            {session.songCount ?? Object.keys(session.playlist || {}).length} songs
                          </span>
                          <span className="text-gray-400 text-xs">
                            {formatDistance(session.distance)} • {session.musicService === 'apple' ? 'Apple Music' : 'Spotify'}
                          </span>
                        </div>

                        {session.nowPlaying?.name && (
                          <p className="text-xs text-gray-400 truncate mb-2">
                            🎵 Now playing: <span className="text-gray-200">{session.nowPlaying.name}</span> — {session.nowPlaying.artist}
                          </p>
                        )}

                        <div className="flex items-center justify-between gap-3 mt-3">
                          <AvatarStack
                            people={session.participantPreview || Object.values(session.participants || {})}
                            max={5}
                            size="sm"
                          />
                          <button
                            onClick={() => handleJoinNearbySession(session)}
                            className="btn-primary text-sm px-5 shrink-0"
                          >
                            Join Session
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-400">
                      No nearby sessions found. Be the first to start one!
                    </p>
                  </div>
                )}
              </motion.div>
            </React.Fragment>
          )}

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-12 grid md:grid-cols-3 gap-6"
          >
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Music className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2">Real-time Playlist</h3>
              <p className="text-gray-400 text-sm">
                Vote and suggest songs in real-time with other party-goers
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2">Geofenced Access</h3>
              <p className="text-gray-400 text-sm">
                Automatically join sessions when you're in the area
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold mb-2">Social Features</h3>
              <p className="text-gray-400 text-sm">
                Chat with other participants and control your visibility
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Welcome; 